import { useEffect } from 'react';
import Modal from '../modal/Modal';
import { useWallet } from '../wallet/WalletProvider';
import { formatDate } from '../../utils/formatDate';
import './ConfirmDialog.css';

const formatTokens = (value) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value);

const Row = ({ label, children }) => (
  <div className="confirm_row">
    <span className="confirm_label">{label}</span>
    <span className="confirm_value">{children}</span>
  </div>
);

// Адрес — отдельной карточкой на всю ширину, а не строкой в столбик значений:
// его читают посимвольно, и перенос по середине строки этому мешает.
const AddressRow = ({ label, address }) => (
  <div className="confirm_address_row">
    <span className="confirm_label">{label}</span>
    <span className="confirm_address">{address}</span>
  </div>
);

/**
 * Последний экран перед кошельком: что именно уйдёт на подпись.
 *
 * Окно ничего не считает и никуда не ходит — показывает готовую заявку. Всё,
 * что здесь видно, отправляется дословно: отправка берёт параметры из этой же
 * заявки, а не перечитывает поля формы.
 *
 * Показывается только перед стейкингом. Апрув уходит в кошелёк сразу: он
 * выдаётся редко и однократно, а сверять в нём нечего — оператор не вводит ни
 * адреса, ни суммы. Решение осознанное, цена записана в docs/decisions.md.
 *
 * Два вида стейкинга — два набора строк. У бессрочного нет ни срока, ни даты
 * разблокировки, ни расчёта выплаты; зато есть то, чего нет у срочного:
 * необратимость. Ради одной общей рамки окна показывать пустые строки «срок:
 * —» было бы хуже, чем развести содержимое.
 */
const ConfirmDialog = ({ request, onConfirm, onCancel }) => {
  const { address, isConnected, isCorrectChain } = useWallet();

  // Отключение кошелька обесценивает заявку целиком: подписывать её нечем.
  useEffect(() => {
    if (!isConnected) onCancel();
  }, [isConnected, onCancel]);

  // Окно обещает, что подпишется ровно показанное. Сменившийся аккаунт или
  // сеть делают это обещание ложным — подтверждать нечего, пока оператор не
  // вернёт кошелёк в прежнее состояние.
  const staleReason = () => {
    if (!isCorrectChain) {
      return 'Сеть в кошельке изменилась. Переключитесь обратно на BSC.';
    }

    if (address.toLowerCase() !== request.sender.toLowerCase()) {
      return 'В кошельке сменился аккаунт — транзакция уйдёт не с того адреса, что показан. Отмените и соберите заявку заново.';
    }

    return '';
  };

  const stale = staleReason();

  const isPerpetual = request.mode === 'perpetual';

  return (
    <Modal
      title={
        isPerpetual ? 'Подтвердите бессрочный стейкинг' : 'Подтвердите стейкинг'
      }
      onClose={onCancel}
      className="confirm_modal"
    >
      <AddressRow label="Получатель" address={request.receiver} />

      <div className="confirm_rows">
        <Row label="Сумма">{formatTokens(request.amount)} EMIVN</Row>

        {isPerpetual ? (
          <>
            <Row label="Ставка">{request.apr}% годовых</Row>
            <Row label="Срок">без срока</Row>
          </>
        ) : (
          <>
            <Row label="Срок">{request.term} дней</Row>
            <Row label="Ставка">{request.rate}%</Row>
            <Row label="К выплате">{formatTokens(request.payout)} EMIVN</Row>
            <Row label="Разблокировка">{formatDate(request.unlockAt)}</Row>
          </>
        )}
      </div>

      <AddressRow label="Отправитель" address={request.sender} />

      {isPerpetual ? (
        <p className="confirm_warning">
          Тело возврату не подлежит: функции вывода тела в контракте нет.
          Выплачиваться будут только проценты. Ставка общая для всех и может
          быть изменена — накопленное до изменения не пересчитывается.
        </p>
      ) : (
        <p className="confirm_note">
          Сумма выплаты и дата разблокировки — ориентировочный расчет. Точные
          значения фиксирует смарт контракт в момент включения транзакции в
          блокчейн.
        </p>
      )}

      {stale && <p className="confirm_stale">{stale}</p>}

      <div className="modal_actions">
        <button type="button" onClick={onCancel} className="confirm_cancel">
          Отменить
        </button>
        <button
          type="button"
          autoFocus
          onClick={onConfirm}
          disabled={Boolean(stale)}
        >
          Подтвердить
        </button>
      </div>
    </Modal>
  );
};

export default ConfirmDialog;
